import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import TextField from '@material-ui/core/TextField'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import {
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import { Button, Identifier, useNotify } from 'react-admin'

import { captureExceptionIfProduction } from 'helpers/utils'
import { addOrUpdateBrokerNoaAcknowledgment, getBrokerNoaAcknowledgments } from '../../api/core'
import { ProductSubscription } from '../../models/subscription'

const DataTable = ({ data }: { data: any }) => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>
            <Typography fontWeight={700}>Broker</Typography>
          </TableCell>
          <TableCell>
            <Typography fontWeight={700}>Acknowledged?</Typography>
          </TableCell>
          <TableCell>
            <Typography fontWeight={700}>Zendesk Link (Optional)</Typography>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((item: any) => (
          <TableRow key={item.id}>
            <TableCell>{item.broker?.name}</TableCell>
            <TableCell>{item.is_acknowledged && '✅'}</TableCell>
            <TableCell>
              <Link href={item.zendesk_url} target="_blank" rel="noopener noreferrer">
                {item.zendesk_url}
              </Link>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

const BrokerAutocomplete = ({ setBroker }: { setBroker: (broker: any) => void }) => {
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let active = true

    if (isEmpty(inputValue)) {
      setOptions([])
      return
    }

    const getBrokerSuggestions = async () => {
      try {
        setLoading(true)
        const response = await fetch(`api/v1/console/brokers?name=${inputValue}`)
        if (response.status !== 200) throw new Error('Error fetching brokers for autocomplete')

        const brokers = await response.json()

        if (active) {
          setOptions(brokers?.results || [])
          setLoading(false)
        }
      } catch (error) {
        setOptions([])
        setLoading(false)
        setInputValue('')
        captureExceptionIfProduction(error)
      }
    }

    if (inputValue?.length >= 2) {
      // fetch suggestions only if user has typed in at least 2 characters
      getBrokerSuggestions()
    }

    return () => {
      active = false
    }
  }, [inputValue])

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option: any) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        /* @ts-ignore */
        <TextField
          {...params}
          label="Broker"
          variant="outlined"
          required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue)
      }}
      onChange={(_, value) => setBroker(value)}
      noOptionsText="No Brokers Found"
      autoComplete
    />
  )
}

const TeamNOAAcknowledgments = (props: any) => {
  const teamId = props.record?.id
  const isCash = props.record?.product_subscription?.product_name === ProductSubscription.CASH

  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState([])

  const [isDialogOpen, setDialogOpen] = useState(false)
  const [broker, setBroker] = useState<any>(null)
  const [zendeskUrl, setZendeskUrl] = useState<String>('')

  const notify = useNotify()

  const getData = async (teamId: Identifier) => {
    setLoading(true)
    try {
      const response = await getBrokerNoaAcknowledgments(teamId)
      if (response.status === 200) {
        setData(response.data)
        setLoading(false)
      } else {
        throw new Error(
          `Request failed with status code ${response.status} and message ${response.statusText}`
        )
      }
    } catch (error) {
      setLoading(false)
      captureExceptionIfProduction(error)
    }
  }

  useEffect(() => {
    getData(teamId)
  }, [teamId])

  if (!isCash) {
    return (
      <Typography variant="body1" component="span">
        This tab is available only for cash teams.
      </Typography>
    )
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="20vh">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Button onClick={() => setDialogOpen(true)} label="Add NOA Acknowledgment">
        <AddCircleIcon />
      </Button>

      {data.length > 0 && (
        <Box my={2}>
          <DataTable data={data} />
        </Box>
      )}

      <Dialog open={isDialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add NOA Acknowledgment</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="20vh">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box>
                <BrokerAutocomplete setBroker={setBroker} />
              </Box>
              <Box mt={2}>
                <TextField
                  label="URL to Zendesk"
                  variant="outlined"
                  value={zendeskUrl}
                  onChange={(event) => setZendeskUrl(event.target.value)}
                  required
                  fullWidth
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} label="Cancel" disabled={isLoading} />
          <Button
            variant="contained"
            disabled={!broker || !teamId || !zendeskUrl || isLoading}
            onClick={async () => {
              try {
                setLoading(true)
                const isOK = await addOrUpdateBrokerNoaAcknowledgment(teamId, broker?.id, zendeskUrl)
                if (isOK) {
                  setDialogOpen(false)
                  setLoading(false)
                  setZendeskUrl('')
                  setBroker(null)
                  notify('Added Acknowledgment', { type: 'success' })
                  getData(teamId)
                } else {
                  throw new Error('Failed to create user.')
                }
              } catch (error) {
                setDialogOpen(false)
                setLoading(false)
                setZendeskUrl('')
                setBroker(null)
                notify(`Oops, something went wrong: ${JSON.stringify(error)}`, { type: 'error' })
                captureExceptionIfProduction(error)
              }
            }}
            label="Add NOA Acknowledgment"
          />
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TeamNOAAcknowledgments
